import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect } from 'react';

// Layouts
import { MainLayout } from '@/components/layout/MainLayout';
import { AuthLayout } from '@/components/layout/AuthLayout';

// Pages
// HomePage removed - all routes require login
import { ChatPage } from '@/pages/ChatPage';
import { DashboardPage } from '@/pages/dashboard/DashboardPage';
import { ProjectRoomPage } from '@/pages/project/ProjectRoomPage';
import { LoginPage } from '@/pages/auth/LoginPage';
// RegisterPage removed - registration disabled (admin-only)
import { OAuthCallbackPage } from '@/pages/auth/OAuthCallbackPage';
import UsersPage from '@/pages/admin/UsersPage';
import LlmConfigsPage from '@/pages/admin/LlmConfigsPage';
import SettingsPage from '@/pages/settings/SettingsPage';
import ProfilePage from '@/pages/profile/ProfilePage';

// Modals
import { CreateProjectModal } from '@/components/modals/CreateProjectModal';
import { DeleteConfirmModal } from '@/components/modals/DeleteConfirmModal';
import { ChatHistoryModal } from '@/components/modals/ChatHistoryModal';

// Components
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';

// Stores
import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';

// Create query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      retry: (failureCount, error) => {
        // Don't retry on 401 (unauthorized) or 403 (forbidden)
        if (error && typeof error === 'object' && 'response' in error) {
          const status = (error as { response?: { status?: number } }).response?.status;
          if (status === 401 || status === 403) {
            return false;
          }
        }
        return failureCount < 1;
      },
      refetchOnWindowFocus: false,
    },
  },
});

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Public Route wrapper (redirect if authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function AppContent() {
  const { setLoading } = useAuthStore();
  const { theme, setTheme } = useUIStore();

  // Initialize auth state
  useEffect(() => {
    // Check if we have stored auth data
    const storedAuth = localStorage.getItem('auth-storage');
    if (storedAuth) {
      try {
        const { state } = JSON.parse(storedAuth);
        if (state?.token) {
          // Token exists, user is authenticated
          setLoading(false);
        } else {
          setLoading(false);
        }
      } catch {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, [setLoading]);

  // Initialize theme
  useEffect(() => {
    setTheme(theme);
  }, [theme, setTheme]);

  return (
    <TooltipProvider>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
          {/* Public Auth Routes - Login only (registration disabled) */}
          <Route
            element={
              <PublicRoute>
                <AuthLayout />
              </PublicRoute>
            }
          >
            <Route path="/login" element={<LoginPage />} />
          </Route>

          {/* Register route disabled - redirect to login */}
          <Route path="/register" element={<Navigate to="/login" replace />} />

          {/* OAuth Callback */}
          <Route path="/auth/callback" element={<OAuthCallbackPage />} />
          <Route path="/auth/:provider/callback" element={<OAuthCallbackPage />} />

          {/* Protected Routes */}
          <Route
            element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/chat" element={<ChatPage />} />
            <Route path="/project/:projectId" element={<ProjectRoomPage />} />
            {/* Admin Routes */}
            <Route path="/admin/users" element={<UsersPage />} />
            <Route path="/admin/llm" element={<LlmConfigsPage />} />
            {/* User Routes */}
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/profile" element={<ProfilePage />} />
          </Route>

          {/* Home redirects to dashboard (login required) */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />

          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        {/* Global Modals */}
        <CreateProjectModal />
        <DeleteConfirmModal />
        <ChatHistoryModal />

        {/* Toast Notifications */}
        <Toaster />
      </BrowserRouter>
    </TooltipProvider>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppContent />
    </QueryClientProvider>
  );
}
